import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const placementLabel = (p) => (p || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const MockupDisplayCard = ({ mockup, isSelected, onSelect }) => {
  if (process.env.NODE_ENV === 'development') console.log("MockupDisplayCard: received mockup prop:", mockup);
  const navigate = useNavigate();

  const sides = useMemo(() => {
    if (mockup.mockup_image_urls && mockup.mockup_image_urls.length > 0) {
      return mockup.mockup_image_urls.map(({ placement, url }) => ({ label: placementLabel(placement), imageUrl: url, kind: 'mockup' }));
    }
    const placements = mockup.placements && mockup.placements.length > 0
      ? mockup.placements
      : [{ placement: mockup.image_placement || 'front', image_url: null }];
    return placements.map((p, i) => ({
      label: placementLabel(p.placement),
      // Always show rendered mockup in normal view; placement image URLs are source designs.
      imageUrl: mockup.mockup_image_url,
      kind: 'mockup',
    }));
  }, [mockup.placements, mockup.image_placement, mockup.mockup_image_url, mockup.mockup_image_urls]);

  const slides = useMemo(() => {
    const mockupSlides = sides.filter((s) => !!s.imageUrl);
    if (mockup.original_image_url) {
      return [
        ...mockupSlides,
        { label: 'Original design', imageUrl: mockup.original_image_url, kind: 'original' },
      ];
    }
    return mockupSlides;
  }, [sides, mockup.original_image_url]);

  const [slideIndex, setSlideIndex] = useState(0);
  const currentSlide = slides[slideIndex];
  const displayImage = currentSlide?.imageUrl || mockup.mockup_image_url;

  const goPrev = (e) => {
    e.stopPropagation();
    setSlideIndex((i) => (i <= 0 ? slides.length - 1 : i - 1));
  };
  const goNext = (e) => {
    e.stopPropagation();
    setSlideIndex((i) => (i >= slides.length - 1 ? 0 : i + 1));
  };

  const placementText = useMemo(() => {
    if (mockup.placements && mockup.placements.length > 0) {
      return mockup.placements.map((p) => placementLabel(p.placement)).join(', ');
    }
    return mockup.image_placement ? placementLabel(mockup.image_placement) : '';
  }, [mockup.placements, mockup.image_placement]);

  const handleSendToPrint = (e) => {
    e.stopPropagation();
    navigate(`/review-mockup/${mockup.id}`);
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-lg p-6 flex flex-col transition-all duration-300 hover:shadow-xl cursor-pointer ${
        isSelected ? 'ring-4 ring-blue-500' : ''
      }`}
      onClick={() => onSelect(mockup.id)}
    >
      <div className="relative w-full pb-[100%] bg-gray-100 rounded-md overflow-hidden mb-4 flex items-center justify-center">
        <img
          src={displayImage}
          alt={currentSlide ? `${currentSlide.label} view` : 'Mockup'}
          className="absolute inset-0 w-full h-full object-contain transition-transform duration-300 hover:scale-105"
        />
        {slides.length > 0 && (
          <span className="absolute top-2 left-2 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded">
            {currentSlide?.label}
          </span>
        )}
        <div className="absolute inset-0 flex items-center justify-between p-2 pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-1">
            {slides.length > 1 && (
              <>
                <button type="button" onClick={goPrev} className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2" aria-label="Previous side">
                  &lt;
                </button>
                <button type="button" onClick={goNext} className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2" aria-label="Next side">
                  &gt;
                </button>
              </>
            )}
          </div>
        </div>
        {slides.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1 pointer-events-none">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full ${i === slideIndex ? 'bg-white ring-1 ring-gray-800' : 'bg-white/60'}`}
              />
            ))}
          </div>
        )}
      </div>
      {slides.length > 1 && <p className="text-xs text-gray-500 mb-2">Use arrows to switch between mockup and original design.</p>}
      <p className="text-gray-800 font-semibold mb-1">Prompt: {mockup.prompt}</p>
      <p className="text-gray-600 text-sm mb-2">T-shirt Model: {mockup.tshirt_model_name}</p>
      <p className="text-gray-600 text-sm mb-2">Variant: {mockup.tshirt_variant_id}</p>
      {(placementText || mockup.image_placement) && (
        <p className="text-gray-600 text-sm mb-2">Placement: {placementText || placementLabel(mockup.image_placement)}</p>
      )}
      {mockup.stitch_color && <p className="text-gray-600 text-sm mb-2">Stitch Color: {mockup.stitch_color}</p>}
      <p className="text-gray-600 text-sm mb-4">Style: {mockup.style}</p>
      <p className="text-gray-800 font-bold mb-4">Price: ${mockup.price ? mockup.price.toFixed(2) : 'N/A'}</p>

      <button
        onClick={handleSendToPrint}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors"
      >
        Proceed to Payment
      </button>
    </div>
  );
};

export default MockupDisplayCard;